import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  BookOpen,
  BrainCircuit,
  LineChart,
  Sparkles,
  GraduationCap,
  Lightbulb,
  Clock,
  BarChart4,
  Users,
  Code,
  Calendar,
  BookOpenCheck,
  Newspaper,
  FileText,
  ChevronDown,
  Mail
} from "lucide-react";
import { StickyNavbar } from "@/components/landing/StickyNavbar";
import { Benefits } from "@/components/landing/Benefits";
import { FAQ } from "@/components/landing/FAQ";
import { SocialLinks } from "@/components/auth/SocialLinks";
import { PromotionalPopup } from "@/components/landing/PromotionalPopup";
import { AffiliateProgramSection } from '@/components/landing/AffiliateProgramSection';
import { AndroidAppPromoPopup } from '@/components/landing/AndroidAppPromoPopup';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

interface FeatureCardProps {
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ title, description, icon: Icon, color }) => (
  <motion.div variants={itemVariants} className="w-full">
    <Card className="h-full shadow-md hover:shadow-lg transition-shadow duration-300 hover-lift">
      <CardContent className="p-6 flex flex-col items-center text-center">
        <div className={`p-3 rounded-full ${color} mb-4 inline-flex animate-float`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <h3 className="text-xl font-semibold mb-2">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </CardContent>
    </Card>
  </motion.div>
);

interface CategoryCardProps {
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  className?: string;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ title, description, icon: Icon, color, className }) => (
  <motion.div 
    variants={itemVariants} 
    className={`flex-1 min-w-[280px] ${className}`}
    whileHover={{ scale: 1.03 }}
    transition={{ type: "spring", stiffness: 300 }}
  >
    <Card className="h-full border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 hover-lift">
      <CardContent className="p-6">
        <div className="flex items-start space-x-4">
          <div className={`p-3 rounded-full ${color} shrink-0 animate-float`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">{title}</h3>
            <p className="text-gray-600 text-sm">{description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

interface ExamCategoryCardProps {
  title: string;
  color: string;
}

const ExamCategoryCard: React.FC<ExamCategoryCardProps> = ({ title, color }) => (
  <motion.div 
    whileHover={{ scale: 1.03 }}
    transition={{ type: "spring", stiffness: 300 }}
    className="rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover-lift"
  >
    <Card className={`h-full border-l-4 ${color} bg-white hover:bg-gray-50`}>
      <CardContent className="p-4">
        <p className="font-medium text-gray-800">{title}</p>
      </CardContent>
    </Card>
  </motion.div>
);

const LandingPage: React.FC = () => {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  // Handle scroll to section
  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-white via-purple-50/30 to-blue-50/30">
      {/* Android App Promotional Popup */}
      <AndroidAppPromoPopup />
      
      {/* Promotional Popup */}
      <PromotionalPopup />
      
      {/* Sticky Navigation */}
      <StickyNavbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-32 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-grid-pattern"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Updated badge with policy-compliant text */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                duration: 0.7, 
                delay: 0.2,
                ease: "easeOut" 
              }}
              className="mb-5"
            >
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-gradient-to-r from-[#FF9933] via-[#FFFFFF] to-[#138808] opacity-20 rounded-lg blur-[2px]"></div>
                <div className="relative px-6 py-2 border border-gray-200 rounded-lg bg-gradient-to-r from-orange-50 via-white to-green-50 shadow-sm">
                  <span className="font-medium text-lg bg-gradient-to-r from-[#FF9933] via-[#000080] to-[#138808] bg-clip-text text-transparent animate-tricolor-pulse">
                    An AI-Powered Adaptive Learning Platform
                  </span>
                </div>
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-[#FF9933] rounded-full animate-pulse"></div>
                <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-[#138808] rounded-full animate-pulse"></div>
              </div>
            </motion.div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 font-playfair">
              <span className="bg-gradient-to-r from-[#8B5CF6] via-[#D946EF] to-[#0EA5E9] bg-clip-text text-transparent animate-fadeIn">
                Edaptica
              </span>
              <span className="block mt-2 text-3xl md:text-4xl text-gray-900 animate-slideInRight">
                Smarter Learning with Real-Time AI Adaptation
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 md:px-10 animate-fadeIn">
              Personalized learning paths, AI-powered analytics, and adaptive quizzes
              that evolve with your knowledge and learning style.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button asChild size="lg" className="bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white px-8 shadow-md button-pop">
                <Link to="/auth">Get Started</Link>
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="border-gray-300 hover-border-glow"
                onClick={() => scrollToSection('features')}
              >
                Explore Features <ChevronDown className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-16 relative max-w-5xl mx-auto"
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-100 hover:shadow-soft-hover transition-all duration-500">
              <img 
                src="/lovable-uploads/9953aac9-ece1-40b2-a7d3-53f2f8f1f5c9.png" 
                alt="Edaptica Dashboard Preview" 
                className="w-full"
              />
            </div>
            <div className="absolute -bottom-5 -right-5 -z-10 w-full h-full bg-gradient-to-r from-purple-200 to-blue-200 rounded-2xl transform rotate-1"></div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="absolute bottom-8 left-0 right-0 flex justify-center"
          >
            <button
              onClick={() => scrollToSection('features')}
              className="flex flex-col items-center text-gray-400 hover:text-primary transition-colors"
            >
              <span className="text-sm mb-1">Scroll to explore</span>
              <ChevronDown className="animate-bounce" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Feature Benefits Section - replacing stats */}
      <section className="py-20 bg-gradient-to-br from-white to-blue-50/30">
        <div className="container mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerVariants}
            className="text-center mb-16"
          >
            <div className="inline-block bg-blue-50 rounded-full px-4 py-1 mb-4">
              <span className="text-blue-600 font-medium text-sm">Key Benefits</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Edaptica</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the power of adaptive learning designed for competitive exam preparation.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center p-6 rounded-xl bg-white shadow-sm border border-gray-100 hover:shadow-md transition-all"
            >
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-full bg-blue-100 text-blue-600 inline-flex">
                  <Users className="h-7 w-7" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Adaptive Learning</h3>
              <p className="text-gray-600 mt-2 font-medium">Real-time difficulty adjustment based on your performance</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-center p-6 rounded-xl bg-white shadow-sm border border-gray-100 hover:shadow-md transition-all"
            >
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-full bg-purple-100 text-purple-600 inline-flex">
                  <BookOpen className="h-7 w-7" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Comprehensive Content</h3>
              <p className="text-gray-600 mt-2 font-medium">Extensive question banks and study materials</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center p-6 rounded-xl bg-white shadow-sm border border-gray-100 hover:shadow-md transition-all"
            >
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-full bg-green-100 text-green-600 inline-flex">
                  <GraduationCap className="h-7 w-7" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">AI-Powered Analytics</h3>
              <p className="text-gray-600 mt-2 font-medium">Detailed insights into your learning patterns</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-center p-6 rounded-xl bg-white shadow-sm border border-gray-100 hover:shadow-md transition-all"
            >
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-full bg-amber-100 text-amber-600 inline-flex">
                  <BarChart4 className="h-7 w-7" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Progress Tracking</h3>
              <p className="text-gray-600 mt-2 font-medium">Monitor your improvement over time</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <Benefits />

      {/* About Edaptica Section */}
      <section id="about-edaptica" className="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="container mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerVariants}
            className="text-center mb-16"
          >
            <div className="inline-block bg-indigo-50 rounded-full px-4 py-1 mb-4">
              <span className="text-indigo-600 font-medium text-sm">Why Edaptica?</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Smarter, Personalized, Real-Time Learning</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Edaptica is an AI-powered adaptive learning platform for aspirants of UPSC, GATE, CDS, Banking, SSC, and other competitive exams.
              Our platform adapts to how you learn — in real-time.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard 
              title="Real-Time Adaptive Quiz Engine"
              description="Start at your level and evolve with every question. Your quiz adjusts dynamically to your progress."
              icon={BrainCircuit}
              color="bg-purple-500"
            />
            <FeatureCard 
              title="Quiz Mistake Flashcards"
              description="Mistakes become personalized flashcards so you master weaknesses without extra effort."
              icon={BookOpenCheck}
              color="bg-blue-500"
            />
            <FeatureCard 
              title="PYQ Multiplier"
              description="Previous Year Questions intelligently recycled with spaced repetition to boost recall."
              icon={Calendar}
              color="bg-cyan-600"
            />
            <FeatureCard 
              title="Adaptive Study Notes"
              description="Focused notes generated based on your quiz performance and weak areas."
              icon={FileText}
              color="bg-teal-500"
            />
            <FeatureCard 
              title="Adaptive Video Learning"
              description="Video content tailored to quiz results. No random playlists — only what matters."
              icon={Newspaper}
              color="bg-rose-500"
            />
            <FeatureCard 
              title="Adaptive Mock Tests"
              description="Mock tests evolve with your level. No two tests are the same — just smarter."
              icon={BarChart4}
              color="bg-orange-500"
            />
            <FeatureCard 
              title="Deep Performance Analytics"
              description="Track your accuracy, weak areas, and learning time with visual insights."
              icon={LineChart}
              color="bg-indigo-500"
            />
            <FeatureCard 
              title="Dual Language Support"
              description="All quizzes, notes, and videos available in English and Hindi."
              icon={BookOpen}
              color="bg-lime-500"
            />
            <FeatureCard 
              title="AI Learning Studio"
              description="Create mind maps, summaries, PPTs, and ask doubts — all with AI."
              icon={Lightbulb}
              color="bg-yellow-500"
            />
          </div>

          <div className="text-center mt-12 max-w-3xl mx-auto text-lg text-gray-700">
            <p className="mb-4">🎯 Distraction-Free Learning: No ads, no clutter — just what improves your learning.</p>
            <p className="mb-4">🎁 Free Trial: Start with a 7-day free trial and explore the full potential of Edaptica.</p>
            <p className="mb-4">🔒 Privacy-First: Your data is secure, encrypted, and never shared.</p>
            <p className="mt-6">Built for competitive exam aspirants across India. Get started and discover your personalized path to exam success.</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerVariants}
            className="text-center mb-16"
          >
            <div className="inline-block bg-purple-50 rounded-full px-4 py-1 mb-4">
              <span className="text-purple-600 font-medium text-sm">Smart Features</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Transform Your Learning Experience</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how Edaptica's intelligent features adapt to your learning style and help you achieve your goals.
            </p>
          </motion.div>
          
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            <FeatureCard 
              title="AI-Powered Analytics"
              description="Get deep insights into your learning patterns with advanced analytics that identify strengths and areas for improvement."
              icon={LineChart}
              color="bg-blue-500"
            />
            <FeatureCard 
              title="Adaptive Quizzes"
              description="Experience quizzes that adapt to your knowledge level, focusing on areas where you need the most practice."
              icon={BrainCircuit}
              color="bg-purple-500"
            />
            <FeatureCard 
              title="Smart Flashcards"
              description="Efficient memorization with spaced repetition flashcards that prioritize what you're about to forget."
              icon={BookOpen}
              color="bg-indigo-500"
            />
            <FeatureCard 
              title="Personalized Learning Paths"
              description="Follow custom learning journeys tailored to your goals, learning style, and progress."
              icon={GraduationCap}
              color="bg-rose-500"
            />
            <FeatureCard 
              title="AI Tutoring"
              description="Get instant help from our AI tutor that understands your specific questions and learning context."
              icon={Lightbulb}
              color="bg-amber-500"
            />
            <FeatureCard 
              title="Adaptive Scheduling"
              description="Study plans that fit your life with smart scheduling that optimizes your learning time."
              icon={Clock}
              color="bg-emerald-500"
            />
            <FeatureCard 
              title="Study Notes"
              description="Organize and access your study materials in one place with smart categorization and search functionality."
              icon={FileText}
              color="bg-teal-500"
            />
            <FeatureCard 
              title="Current Affairs"
              description="Stay updated with the latest developments relevant to your field with curated current affairs content and quizzes."
              icon={Newspaper}
              color="bg-cyan-600"
            />
          </motion.div>
        </div>
      </section>

      {/* Exam Categories Section */}
      <section id="exams" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerVariants}
            className="text-center mb-16"
          >
            <div className="inline-block bg-amber-50 rounded-full px-4 py-1 mb-4">
              <span className="text-amber-600 font-medium text-sm">Specialized Learning Paths</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Tailored for Your Exam Goals</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Edaptica provides specialized learning paths for various competitive exams and professional certifications.
            </p>
          </motion.div>
          
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={containerVariants}
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-w-6xl mx-auto"
          >
            <ExamCategoryCard title="JEE Genius (IIT-JEE, Mains & Advanced)" color="border-blue-500" />
            <ExamCategoryCard title="NEET Mastery (Medical Entrance)" color="border-green-500" />
            <ExamCategoryCard title="GATE Pathfinder" color="border-indigo-500" />
            <ExamCategoryCard title="UPSC Visionary (Civil Services Exam)" color="border-red-500" />
            <ExamCategoryCard title="Govt Exam Wizard" color="border-orange-500" />
            <ExamCategoryCard title="Data Science Ace" color="border-purple-500" />
            <ExamCategoryCard title="Coding Maestro" color="border-emerald-500" />
            <ExamCategoryCard title="CyberSecure Pro" color="border-cyan-600" />
            <ExamCategoryCard title="Cloud Architect" color="border-sky-500" />
            <ExamCategoryCard title="Banking Whiz" color="border-amber-500" />
            <ExamCategoryCard title="Defence IQ" color="border-gray-700" />
            <ExamCategoryCard title="Judiciary Mindset" color="border-pink-500" />
            <ExamCategoryCard title="Teaching Excellence" color="border-lime-500" />
            <ExamCategoryCard title="Stock Market Wizard" color="border-green-600" />
            <ExamCategoryCard title="Legal Mindset" color="border-rose-500" />
            <ExamCategoryCard title="Entrepreneurial IQ" color="border-teal-500" />
            <ExamCategoryCard title="Digital Marketing Pro" color="border-blue-600" />
            <ExamCategoryCard title="Civil Exam Preparation" color="border-yellow-600" />
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center mt-12"
          >
            <Button asChild size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 shadow-md button-pop">
              <Link to="/auth">Start Your Exam Preparation</Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Categories Carousel */}
      <section id="categories" className="py-20 bg-gradient-to-b from-white to-purple-50/30">
        <div className="container mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerVariants}
            className="text-center mb-12"
          >
            <div className="inline-block bg-blue-50 rounded-full px-4 py-1 mb-4">
              <span className="text-blue-600 font-medium text-sm">Learning Categories</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Explore Learning Categories</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our comprehensive suite of learning tools across various categories.
            </p>
          </motion.div>
          
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full max-w-5xl mx-auto"
          >
            <CarouselContent className="py-4">
              <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                <CategoryCard
                  title="Exam Analytics"
                  description="Track your performance, identify patterns, and receive personalized recommendations."
                  icon={BarChart4}
                  color="bg-blue-500"
                />
              </CarouselItem>
              <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                <CategoryCard
                  title="Community Learning"
                  description="Connect with peers, join study groups, and collaborate on difficult concepts."
                  icon={Users}
                  color="bg-purple-500"
                />
              </CarouselItem>
              <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                <CategoryCard
                  title="Code Playground"
                  description="Practice coding with interactive environments and real-time feedback."
                  icon={Code}
                  color="bg-emerald-500"
                />
              </CarouselItem>
              <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                <CategoryCard
                  title="Smart Scheduling"
                  description="Optimize your study time with AI-generated schedules based on your learning patterns."
                  icon={Calendar}
                  color="bg-amber-500"
                />
              </CarouselItem>
              <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                <CategoryCard
                  title="Flashcard System"
                  description="Master concepts with spaced repetition flashcards that adapt to your recall strength."
                  icon={BookOpenCheck}
                  color="bg-indigo-500"
                />
              </CarouselItem>
              <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                <CategoryCard
                  title="Current Affairs"
                  description="Stay updated with the latest developments in your field with curated content."
                  icon={Newspaper}
                  color="bg-rose-500"
                />
              </CarouselItem>
            </CarouselContent>
            <div className="flex justify-center mt-8 gap-2">
              <CarouselPrevious className="static transform-none mx-1" />
              <CarouselNext className="static transform-none mx-1" />
            </div>
          </Carousel>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerVariants}
            className="text-center mb-16"
          >
            <div className="inline-block bg-indigo-50 rounded-full px-4 py-1 mb-4">
              <span className="text-indigo-600 font-medium text-sm">Smart Methodology</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How Edaptica Adapts to You</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our platform uses AI to understand your unique learning style and needs.
            </p>
          </motion.div>
          
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            <motion.div variants={itemVariants} className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mb-6 animate-float">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Assessment</h3>
              <p className="text-gray-600">
                We analyze your learning patterns, strengths, and areas for growth through adaptive quizzes.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants} className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center mb-6 animate-float">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Personalization</h3>
              <p className="text-gray-600">
                Our AI creates tailored learning paths and recommendations based on your unique needs.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants} className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-orange-600 rounded-full flex items-center justify-center mb-6 animate-float">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Continuous Adaptation</h3>
              <p className="text-gray-600">
                As you learn, our system evolves with you, adjusting difficulty and focus areas in real-time.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ />

      {/* Affiliate Program Section */}
      <AffiliateProgramSection />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="p-8 md:p-12 relative">
              <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-gradient-to-br from-purple-300/40 to-blue-300/40 rounded-full blur-2xl"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-center mb-8">
                  <Sparkles className="h-10 w-10 text-purple-500 animate-pulse" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">
                  Ready to Transform Your Learning?
                </h2>
                <p className="text-xl text-gray-600 text-center mb-8 max-w-2xl mx-auto">
                  Get started with Edaptica's personalized learning tools and
                  adaptive AI-powered platform designed for exam success.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button asChild size="lg" className="bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white px-8 button-pop">
                    <Link to="/auth">Get Started for Free</Link>
                  </Button>
                  <Button variant="outline" size="lg" className="hover-border-glow">
                    <Link to="/auth">Explore Features</Link>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12 border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <div className="font-bold text-2xl mb-2 text-gray-900 flex items-center">
                <div className="w-8 h-8 mr-2">
                  <img 
                    src="/lovable-uploads/8dd4efad-6e3c-4413-9e65-4c7880387263.png" 
                    alt="Edaptica Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
                Edaptica
              </div>
              <p className="text-gray-600 text-sm">Smarter Learning with Real-Time AI Adaptation</p>
            </div>
            <div className="flex flex-col md:flex-row gap-4 md:gap-8 items-center">
              <Link to="/auth" className="text-gray-600 hover:text-purple-600 transition-colors">Sign In</Link>
              <Link to="/auth" className="text-gray-600 hover:text-purple-600 transition-colors">Register</Link>
              <a href="#features" className="text-gray-600 hover:text-purple-600 transition-colors">Features</a>
              <a href="#exams" className="text-gray-600 hover:text-purple-600 transition-colors">Exams</a>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-10">
            <div>
              <h3 className="font-medium mb-4 text-gray-800">Company</h3>
              <ul className="space-y-2">
                <li><Link to="/about-us" className="text-gray-600 hover:text-purple-600 transition-colors">About Us</Link></li>
                <li><Link to="/contact-us" className="text-gray-600 hover:text-purple-600 transition-colors">Contact Us</Link></li>
                <li><Link to="/pricing" className="text-gray-600 hover:text-purple-600 transition-colors">Pricing</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-medium mb-4 text-gray-800">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/privacy-policy" className="text-gray-600 hover:text-purple-600 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-and-conditions" className="text-gray-600 hover:text-purple-600 transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/cookie-policy" className="text-gray-600 hover:text-purple-600 transition-colors">Cookie Policy</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-medium mb-4 text-gray-800">More Info</h3>
              <ul className="space-y-2">
                <li><Link to="/accessibility-statement" className="text-gray-600 hover:text-purple-600 transition-colors">Accessibility</Link></li>
                <li><Link to="/cancellation-policy" className="text-gray-600 hover:text-purple-600 transition-colors">Cancellation Policy</Link></li>
                <li><Link to="/gdpr-compliance" className="text-gray-600 hover:text-purple-600 transition-colors">GDPR Compliance</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-medium mb-4 text-gray-800">Connect</h3>
              <SocialLinks />
              <div className="mt-4">
                <a href="mailto:support@edaptica.com" className="text-gray-600 hover:text-purple-600 transition-colors flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  support@edaptica.com
                </a>
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-100 text-center text-gray-500 text-sm">
            <p>© {new Date().getFullYear()} Cibeto Technologies Private Limited. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
